import { useState, useEffect, useRef } from 'react';

export type GlobalUserStatus = 'online' | 'away' | 'offline';

const AWAY_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes of inactivity → away

/**
 * Global hook that tracks the current user's online status based on
 * page visibility and user activity. Use this everywhere an avatar
 * needs to show the user's real-time status.
 */
export function useUserStatus(): GlobalUserStatus {
  const [status, setStatus] = useState<GlobalUserStatus>('online');
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    const markActive = () => {
      lastActivityRef.current = Date.now();
      if (document.visibilityState === 'visible') {
        setStatus('online');
      }
    };

    const checkActivity = () => {
      if (document.visibilityState === 'hidden') {
        setStatus('away');
        return;
      }
      const elapsed = Date.now() - lastActivityRef.current;
      setStatus(elapsed > AWAY_TIMEOUT_MS ? 'away' : 'online');
    };

    const onVisChange = () => {
      if (document.visibilityState === 'visible') {
        markActive();
      } else {
        setStatus('away');
      }
    };

    // Activity events
    const events = ['mousemove', 'keydown', 'pointerdown', 'scroll', 'touchstart'] as const;
    for (const ev of events) {
      window.addEventListener(ev, markActive, { passive: true });
    }
    document.addEventListener('visibilitychange', onVisChange);

    // Check periodically
    timerRef.current = setInterval(checkActivity, 30_000);

    return () => {
      for (const ev of events) {
        window.removeEventListener(ev, markActive);
      }
      document.removeEventListener('visibilitychange', onVisChange);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return status;
}
